import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { ColumnDef } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";

import {
  CheckedItems,
  OutputFormat,
  SetCheckedItems,
  setOutputFormat,
} from "pages/geoscape-batch/models";
import { InputBatchConfigFormat } from "pages/geoscape-batch/types";
import { Dispatch, SetStateAction } from "react";
import { MANDATORY_ATTRIBUTES, addIndex, transformString } from "./utils";

type IndexedInput = InputBatchConfigFormat & { index: number };

interface BuildInputColumnsParams {
  setInPutParameters: Dispatch<SetStateAction<InputBatchConfigFormat[]>>;
}

export const buildInputColumns = ({
  setInPutParameters,
}: BuildInputColumnsParams): ColumnDef<IndexedInput>[] => [
  {
    accessorKey: "index",
    header: "#",
    size: 50,
  },
  {
    accessorKey: "name",
    header: "Name",
    size: 250,
    cell: ({ row }) => {
      return (
        <Input
          id={`input-name-${row.original.index}`}
          value={(row.getValue("name") as string) ?? ""}
          onChange={(e) => {
            const newName = e.target.value;
            setInPutParameters((prev) =>
              prev.map((item, i) =>
                i === row.original.index - 1
                  ? { ...item, name: newName }
                  : item,
              ),
            );
          }}
        />
      );
    },
  },
  {
    accessorKey: "apiParameter",
    header: "Api Parameter",
    size: 250,
    cell: ({ row }) => (
      <Input
        id={`input-api-${row.original.index}`}
        value={row.getValue("apiParameter") as string}
        disabled={row.getValue("apiParameter") === "Ignore"}
        readOnly
      />
    ),
  },
];

interface BuildColumnsParams {
  setEnrichmentRows: setOutputFormat;
  setCheckedItems: SetCheckedItems;
}

export const buildColumns = ({
  setEnrichmentRows,
  setCheckedItems,
}: BuildColumnsParams): ColumnDef<OutputFormat, unknown>[] => {
  const handleDeleteColumn = (row: OutputFormat, index: number) => {
    setEnrichmentRows((prev) => {
      const updated = [...prev];
      updated.splice(index - 1, 1);
      return addIndex(updated);
    });

    setCheckedItems((prev: CheckedItems) => ({
      ...prev,
      [row.property]: {
        ...prev[row.property],
        [`${row.attributeName}`]: false,
      },
    }));
  };

  return [
    {
      enableSorting: false,
      accessorKey: "index",
      header: "#",
      size: 50,
    },
    {
      enableSorting: false,
      accessorKey: "name",
      header: "Name",
      size: 220,
      cell: ({ row }) => {
        return (
          <Input
            id={`enrichment-name-${row.original.index}`}
            value={(row.getValue("name") as string) ?? ""}
            onChange={(e) => {
              const newName = e.target.value;
              setEnrichmentRows((prev) =>
                prev.map((item, i) =>
                  i === (row.original.index as number) - 1
                    ? { ...item, name: newName }
                    : item,
                ),
              );
            }}
          />
        );
      },
    },

    {
      enableSorting: false,
      accessorKey: "attributeName",
      header: "Attribute Name",
      size: 220,
      cell: ({ row }) => (
        <Input
          id={`enrichment-attr-${row.original.index}`}
          value={transformString(row.getValue("attributeName") as string)}
          readOnly
        />
      ),
    },
    {
      accessorKey: "actions",
      header: "",
      size: 60,
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive"
              onClick={() => {
                handleDeleteColumn(row.original, row.original.index as number);
              }}
              disabled={MANDATORY_ATTRIBUTES[row.original.property]?.includes(
                row.original.attributeName,
              )}
            >
              <Trash2 />
            </Button>
          </div>
        );
      },
    },
  ];
};
